import React, { useContext, useState } from "react";
import { Modal, Form, Input, Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import DataContext from "../../../context/DataContext";
import AuthContext from "../../../context/AuthContext";

interface Props {
  open: boolean;
  onClose: () => void;
  idClient: string;
  idCustomer: string;
  onUploadSuccess?: () => void;
  onUploadError?: () => void;
}

export const FileUploadModalClient: React.FC<Props> = ({
  open,
  onClose,
  idClient,
  idCustomer,
  onUploadSuccess,
  onUploadError,
}) => {
  const { dataLoading, uploadFile } = useContext(DataContext);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [values, setValues] = useState<{
    name: string | null;
    link: string | null;
    customer: string | null;
    project: string | null;
  }>({
    name: null,
    link: null,
    customer: null,
    project: null,
  });
  const [file, setFile] = useState<File | null>(null);

  const handleOk = async () => {
    try {
      await form.validateFields();
      if (!file && !values.link) {
        return message.warning("Please provide a file or external link.");
      }

      setUploading(true);
      const formData = new FormData();
      values.name && formData.append("name", values.name);
      values.link && formData.append("link", values.link);
      formData.append("public", true as any);
      file && formData.append("file", file);
      values.project && formData.append("projectId", values.project);

      uploadFile({
        endpoint: `customer/${idClient}/${idCustomer}/upload`,
        formData,
        next: () => {
          setUploading(false);
          message.success("File uploaded!");
          onUploadSuccess?.();
          setFile(null);
          form.resetFields();
          handleCancel();
        },
        onError: () => {
          setUploading(false);
          message.error("Upload failed.");
          setFile(null);
          onClose();
        },
      });
    } catch (error) {
      message.error("Upload failed.");
      setUploading(false);
      setFile(null);
      onUploadError?.();
    }
  };

  const handleCancel = () => {
    setFile(null);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Upload File"
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={uploading}
      okText="Upload"
      cancelText="Cancel"
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="File Name"
          name="name"
          rules={[{ required: true, message: "File name is required" }]}
        >
          <Input
            placeholder="Enter file name"
            onChange={(e) => {
              setValues((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
          />
        </Form.Item>

        <Form.Item
          label="External Link"
          name="link"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                try {
                  if (value.length > 1000) {
                    return Promise.reject(new Error("Link is too long"));
                  }
                  const url = new URL(value);
                  const suspiciousTlds = [".top", ".xyz", ".buzz", ".click"];
                  if (
                    suspiciousTlds.some((tld) => url.hostname.endsWith(tld))
                  ) {
                    return Promise.reject(
                      new Error("Suspicious domain extension detected"),
                    );
                  }
                  if (url.hostname.length > 50) {
                    return Promise.reject(new Error("Domain name is too long"));
                  }
                  if (url.protocol !== "https:") {
                    return Promise.reject(
                      new Error("URL must use HTTPS protocol."),
                    );
                  }
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error("Invalid URL format."));
                }
              },
            },
          ]}
        >
          <Input
            placeholder="https://example.com/file.pdf"
            onChange={(e) => {
              setValues((prev) => ({
                ...prev,
                link: e.target.value,
              }));
            }}
          />
        </Form.Item>

        <Form.Item label="Upload File" name="file">
          <Upload
            name="file"
            accept=".pdf, .docx, .doc, .xlsx, .xls, .csv"
            showUploadList={true}
            beforeUpload={(file) => {
              setFile(file);
              return false;
            }}
            onRemove={() => setFile(null)}
            maxCount={1}
          >
            <Button
              icon={<UploadOutlined />}
              style={{ marginLeft: "2px" }}
              loading={dataLoading}
            >
              Upload File
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
